import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  RouterProvider,
  useLocation,
  useNavigationType
} from 'react-router-dom';
import * as Sentry from '@sentry/react';
import './app.css';

import { ErrorBoundary, AppComponent, Component as Root } from '~/routes/layouts/root';
import { RoutingConstants } from './apps/smartsend/lib/consts/RoutingConstants';

// SmartSend / MyAccount
import MyAccount from './routes/layouts/my-account/my-account';
import SmartSendLayout from './routes/layouts/my-account/smartsend-layout';
import ConsumerLayout from './routes/layouts/my-account/consumer-layout';
import SettingsLayout from './routes/layouts/my-account/account';
import OrdersLayout from './routes/layouts/my-account/orders';
import SupportLayout from './routes/layouts/my-account/support';
import ToolsLayout from './routes/layouts/my-account/tools';
import RewardsLayout from './routes/layouts/my-account/rewards';
import SmartSendDashboard from '~/routes/analytics/dashboard';
import SmartSendOnboarding from '~/routes/smartsend/setup';

// My Account
import AbortSetup from '~/routes/smartsend/abort-setup';
import PickAndPacking from '~/routes/smartsend/pick-and-packing';
import MarketplaceTracking from '~/routes/smartsend/marketplace-tracking';
import RuleManager from '~/routes/rulemanager/rulemanager';
import Manifests from '~/routes/smartsend/manifests';
import CustomerFeatures from '~/routes/customer-features/customer-features';

// Mini Apps
import PickingApp from '~/routes/mini-apps/picking-app';
import PickingList from '~/routes/mini-apps/picking-list';
import TariffLookup from '~/routes/mini-apps/tariff-codes';

// Other
import SignIn from '~/routes/signin';
import Silent from '~/routes/silent';
import { childrenOfOrders } from './routes/groups/orders';
import { childrenOfSupport } from './routes/groups/support';
import { childrenOfAccount } from './routes/groups/account';
import { childrenOfTools } from './routes/groups/tools';
import { consumerRoutes } from './routes/groups/consumer';
import { legacyRoutes } from './routes/groups/legacy';
import LegacyRoute from './routes/legacy-route';
import { childrenOfRewards } from './routes/groups/rewards';

Sentry.init({
  environment: import.meta.env.VITE_ENVIRONMENT || 'Debug',
  release: import.meta.env.VITE_VERSION || '0.0.0',
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      networkDetailAllowUrls: [
        window.location.origin,
        'https://smartsend-edge.serverless.p2g.systems',
        'https://me.serverless.p2g.systems'
      ],
      networkResponseHeaders: [
        'x-tenant',
        'x-region',
        'x-trace',
        'x-cache',
        'cf-cache-status',
        'x-amzn-trace-id',
        'cache-control'
      ],
      unblock: ['.sentry-unblock, [data-sentry-unblock]'],
      unmask: ['.sentry-unmask, [data-sentry-unmask]']
    }),
    Sentry.feedbackIntegration({
      autoInject: false
    }),
    Sentry.thirdPartyErrorFilterIntegration({
      filterKeys: ['parcel2go-uiz'],
      behaviour: 'drop-error-if-contains-third-party-frames'
    }),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    })
  ],
  // Performance Monitoring
  tracesSampleRate: 0.01, //  Capture 100% of the transactions
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

const router = sentryCreateBrowserRouter([
  {
    path: ':locale?/:prefix?',
    element: <Root />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: ':prefix/',
        element: <MyAccount />,
        children: [
          // Existing (SmartSend) experience - all current routes, unchanged paths,
          // wrapped in the extracted chrome layout (pathless, adds no URL segment).
          {
            element: <SmartSendLayout />,
            children: [
              {
                path: '',
                element: <LegacyRoute r={RoutingConstants.OrderHistory} />
              },
              {
                path: RoutingConstants.Analytics,
                element: <SmartSendDashboard />
              },
              {
                path: RoutingConstants.RuleManager,
                element: <RuleManager />
              },
              {
                path: RoutingConstants.SmartSendSetupAbort,
                element: <AbortSetup />
              },
              {
                path: RoutingConstants.SmartSendSetup,
                element: <SmartSendOnboarding />
              },
              {
                path: RoutingConstants.AwaitingDispatch,
                element: <MarketplaceTracking />
              },
              {
                path: RoutingConstants.PackageContents,
                element: <PickAndPacking />
              },
              {
                path: RoutingConstants.Manifests,
                element: <Manifests />
              },
              {
                path: RoutingConstants.CustomerFeatures,
                element: <CustomerFeatures />
              },
              // Areas
              {
                path: '',
                element: <OrdersLayout />,
                children: childrenOfOrders
              },
              {
                path: '',
                element: <SettingsLayout />,
                children: childrenOfAccount
              },
              {
                path: '',
                element: <SupportLayout />,
                children: childrenOfSupport
              },
              {
                path: '',
                element: <ToolsLayout />,
                children: childrenOfTools
              },
              {
                path: '',
                element: <RewardsLayout />,
                children: childrenOfRewards
              },
              // Legacy
              {
                path: '',
                children: legacyRoutes
              }
            ]
          },
          // Consumer "My Account" view - additive, nested under the account/ segment.
          // The brief-aligned IA (My Orders / Support / Communications / PrePay & Balance /
          // Account) is defined in groups/consumer.tsx.
          {
            path: RoutingConstants.ConsumerRoot,
            element: <ConsumerLayout />,
            children: consumerRoutes
          }
        ]
      },
      {
        path: ':prefix/',
        element: <AppComponent />,
        children: [
          {
            path: RoutingConstants.AppPickingList,
            element: <PickingList />,
            errorElement: <ErrorBoundary />
          },
          {
            path: RoutingConstants.AppMobilePicking,
            element: <PickingApp />,
            errorElement: <ErrorBoundary />
          },
          {
            path: RoutingConstants.AppTariffCodes,
            element: <TariffLookup />,
            errorElement: <ErrorBoundary />
          }
        ]
      }
    ]
  },
  {
    path: '/ui/statics/signin-callback',
    element: <SignIn />
  },
  {
    path: 'ui/statics/myaccount-signin-callback',
    element: <SignIn />
  },
  {
    path: '/ui/statics/silent-callback',
    element: <Silent />
  },
  {
    path: 'ui/statics/myaccount-silent-callback',
    element: <Silent />
  }
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
      <RouterProvider router={router} />
    </Sentry.ErrorBoundary>
  </StrictMode>
);
